import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Button, Input, InputGroup, InputProps} from 'reactstrap';
import {InputGroupProps} from 'reactstrap/types/lib/InputGroup';
import {ButtonProps} from 'reactstrap/types/lib/Button';
import classnames from '@src/helpers/classnames';
import trans from '@src/helpers/trans';
import RenderIf from '@src/components/RenderIf/RenderIf';
import {LocaleContext} from '@src/contexts/LocaleContext';

type ListSearchField = Omit<InputProps, 'onSubmit' | 'onInput' | 'value' | 'defaultValue'> & {
    defaultValue?: string | null,
    onSubmit?: (searchTerm: string | null) => void,
    inputGroupProps?: InputGroupProps
    clearButtonProps?: ButtonProps | false,
    submitButtonProps?: ButtonProps | false,
};

export default function ListSearchField(props: ListSearchField) {
    const {
        inputGroupProps = {},
        clearButtonProps = {},
        submitButtonProps = {},
        defaultValue = null,
        onSubmit,
        className = '',
        placeholder,
        ...rest
    } = props;

    const {locale} = useContext(LocaleContext);
    const [searchTerm, setSearchTerm] = useState<string | null>(defaultValue);

    useEffect(() => void setSearchTerm(defaultValue), [defaultValue]);

    const shouldRenderClearButton = props.clearButtonProps !== false;
    const shouldRenderSubmitButton = props.submitButtonProps !== false;

    const [onSubmitListener, onResetListener] = useMemo(() => {
        const onSubmitListener: React.FormEventHandler = e => {
            e.preventDefault();

            if (onSubmit) {
                onSubmit(searchTerm ?? null);
            }
        };

        const onResetListener = () => {
            setSearchTerm(null);

            if (onSubmit) {
                onSubmit(null);
            }
        };

        return [onSubmitListener, onResetListener];
    }, [onSubmit, searchTerm]);

    return <form className={classnames({[`${className}-form`]: className})} onSubmit={onSubmitListener}>
        <InputGroup {...inputGroupProps}>
            <Input
                    type="text"
                    className={className}
                    {...rest}
                    value={searchTerm ?? ''}
                    onInput={e => setSearchTerm(e.currentTarget.value)}
                    placeholder={placeholder ? trans(placeholder, locale) : undefined}
            />

            <RenderIf expression={shouldRenderClearButton}>
                <Button type="button" color="link" {...clearButtonProps} onClick={onResetListener}>
                    <i className="fa fa-times"/>
                </Button>
            </RenderIf>

            <RenderIf expression={shouldRenderSubmitButton}>
                <Button type="submit" color="link" {...submitButtonProps}>
                    <i className="fa fa-search"/>
                </Button>
            </RenderIf>
        </InputGroup>
    </form>;
}
